import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Avatar,
  Stack,
  CircularProgress,
  Grid,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  PlayArrow as ClockInIcon,
  Stop as ClockOutIcon,
  CheckCircle as CompleteIcon,
  RateReview as ReviewIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as ClientIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { Shift, ShiftCompletion, rosterService } from '../../services/rosterService';
import { clientService } from '../../services/clientService';
import { staffService } from '../../services/staffService';
import { formatDateTimeForDisplay, formatDateTimeForUserTimezone, getUserTimezone } from '../../utils/dateUtils';
import { getShiftStatusInfo, getShiftStatusColor, getShiftStatusLabel, getUnifiedShiftStatus } from '../../utils/shiftStatusUtils';
import { getStaffDisplayName, getClientDisplayName, enrichShiftsWithNames } from '../../utils/nameUtils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import PageLayout from '../../components/Layout/PageLayout';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface ShiftManagementPageProps {
  currentUserId: number;
  userRole: 'staff' | 'administrator' | 'manager' | 'supervisor';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`shift-tabpanel-${index}`}
      aria-labelledby={`shift-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ShiftManagementPage: React.FC<ShiftManagementPageProps> = ({ currentUserId, userRole }) => {
  // Helper function to check if user has admin privileges
  const hasAdminPrivileges = () => {
    return userRole === 'administrator' || userRole === 'manager' || userRole === 'supervisor';
  };

  const [tabValue, setTabValue] = useState(0);
  const [activeShifts, setActiveShifts] = useState<Shift[]>([]);
  const [completedShifts, setCompletedShifts] = useState<ShiftCompletion[]>([]);
  const [pendingReviewShifts, setPendingReviewShifts] = useState<ShiftCompletion[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedShift, setSelectedShift] = useState<Shift | ShiftCompletion | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isTimeEditModalOpen, setIsTimeEditModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [editTimeData, setEditTimeData] = useState({
    actualStartTime: '',
    actualEndTime: '',
    notes: ''
  });
  const [reviewData, setReviewData] = useState({
    isAccepted: true,
    adminReviewNotes: ''
  });

  // Staff edit-shift-info dialog state
  const [editInfoDialog, setEditInfoDialog] = useState<{ open: boolean; shift?: any }>({ open: false });
  const [editInfoWorkInfo, setEditInfoWorkInfo] = useState('');
  const [editInfoTravelKm, setEditInfoTravelKm] = useState('');
  const [editInfoError, setEditInfoError] = useState<string | null>(null);
  const [editInfoSaving, setEditInfoSaving] = useState(false);

  useEffect(() => {
    loadShiftData();
  }, [tabValue, currentUserId, userRole]);

  const loadShiftData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      switch (tabValue) {
        case 0: // Active Shifts
          await loadActiveShifts();
          break;
        case 1: // Completed Shifts
          await loadCompletedShifts();
          break;
        case 2: // Pending Review (Admin only)
          if (hasAdminPrivileges()) {
            await loadPendingReviewShifts();
          }
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  const loadActiveShifts = async () => {
    try {
      let shifts: Shift[] = [];
      
      if (hasAdminPrivileges()) {
        // Admin: Load all active shifts (exclude completed, pending_review, and inactive shifts)
        const response = await rosterService.getShifts({
          status: 'scheduled,in_progress'
        });
        const allShifts = Array.isArray(response) ? response : response.data || [];
        
        // Additional client-side filtering to ensure only truly active shifts
        shifts = allShifts.filter(shift => 
          shift.status === 'scheduled' || shift.status === 'in_progress'
        );
      } else {
        // Staff: Load only their active shifts (exclude completed and pending_review)
        const response = await rosterService.getStaffShifts(currentUserId);
        const allShifts = response.data;
        shifts = allShifts.filter(shift => 
          shift.status === 'in_progress' || shift.status === 'scheduled'
        );
      }

      // Enrich active shifts with client and staff details
      const enrichedShifts = await Promise.all(
        shifts.map(async (shift) => await enrichShiftWithDetails(shift))
      );
      
      setActiveShifts(enrichedShifts);
    } catch (error) {
      console.error('Error loading active shifts:', error);
      setActiveShifts([]);
    }
  };

  const loadCompletedShifts = async () => {
    try {
      let completedShifts: any[] = [];
      
      if (hasAdminPrivileges()) {
        // Admin: Load ONLY shifts with 'completed' status (i.e., reviewed and approved)
        
        // Get shifts with 'completed' status only
        const completedStatusResponse = await rosterService.getShifts({
          status: 'completed'
        });
        const completedStatusShifts = Array.isArray(completedStatusResponse) ? completedStatusResponse : completedStatusResponse.data || [];
        
        // Convert regular completed shifts to completion format for consistency
        const formattedCompletedShifts = completedStatusShifts.map(shift => ({
          shiftId: shift.id,
          staffId: shift.staffId,
          clientId: shift.clientId,
          startDateTime: shift.startDateTime,
          endDateTime: shift.endDateTime,
          status: 'completed',
          isCompletedByStaff: true,
          completedByStaffAt: shift.actualEndTime || shift.updatedAt || shift.createdAt,
          isReviewedByAdmin: true, // These are completed, so they must be reviewed
          isAcceptedByAdmin: true, // These are completed, so they must be accepted
          createdAt: shift.createdAt,
          updatedAt: shift.updatedAt,
          ...shift
        }));
        
        completedShifts = formattedCompletedShifts;
      } else {
        // Staff: Load ONLY their shifts with 'completed' status (i.e., reviewed and approved)
        const response = await rosterService.getStaffShifts(currentUserId);
        const allShifts = response.data;
        const staffCompletedShifts = allShifts.filter(shift => 
          shift.status === 'completed'
        );
        
        // Convert status-based completed shifts to completion format for consistency
        const formattedCompletedShifts = staffCompletedShifts.map(shift => ({
          shiftId: shift.id,
          staffId: shift.staffId,
          clientId: shift.clientId,
          startDateTime: shift.startDateTime,
          endDateTime: shift.endDateTime,
          status: 'completed',
          isCompletedByStaff: true,
          completedByStaffAt: shift.actualEndTime || shift.updatedAt || shift.createdAt,
          isReviewedByAdmin: true, // These are completed, so they must be reviewed
          isAcceptedByAdmin: true, // These are completed, so they must be accepted
          createdAt: shift.createdAt,
          updatedAt: shift.updatedAt,
          ...shift
        }));
        
        completedShifts = formattedCompletedShifts;
      }

      // Enhanced enrichment with client/staff service calls
      const enrichedShifts = await Promise.all(
        completedShifts.map(async (completedShift) => {
          try {
            // First try to get full shift data from the shifts endpoint
            const fullShiftResponse = await rosterService.getShift(completedShift.shiftId);
            const fullShift = ('data' in fullShiftResponse ? fullShiftResponse.data : fullShiftResponse) as Shift;
            
            let mergedShift: any = { ...completedShift };
            
            // Merge with full shift data if available
            if (fullShift && (fullShift.id || fullShift.clientId || fullShift.staffId)) {
              mergedShift = {
                ...completedShift,
                clientId: fullShift.clientId || completedShift.clientId,
                staffId: fullShift.staffId || completedShift.staffId,
                startDateTime: fullShift.startDateTime || completedShift.startDateTime,
                endDateTime: fullShift.endDateTime || completedShift.endDateTime,
                // Add optional fields from full shift if they exist
                ...(fullShift.actualStartTime && { actualStartTime: fullShift.actualStartTime }),
                ...(fullShift.actualEndTime && { actualEndTime: fullShift.actualEndTime }),
                ...(fullShift.clientName && { clientName: fullShift.clientName }),
                ...(fullShift.staffName && { staffName: fullShift.staffName }),
                ...(fullShift.clientDetails && { clientDetails: fullShift.clientDetails }),
                ...(fullShift.staffDetails && { staffDetails: fullShift.staffDetails }),
                ...(fullShift.shiftType && { shiftType: fullShift.shiftType }),
                ...(fullShift.notes && { notes: fullShift.notes }),
                ...(fullShift.careInstructions && { careInstructions: fullShift.careInstructions }),
                ...(fullShift.hourlyRate && { hourlyRate: fullShift.hourlyRate }),
                ...(fullShift.totalAmount && { totalAmount: fullShift.totalAmount })
              };
            }
            
            // Now enrich with client/staff details if still missing
            return await enrichShiftWithDetails(mergedShift);
            
          } catch (error) {
            console.warn(`Could not enrich shift ${completedShift.shiftId}:`, error);
            // If all else fails, at least try to get client/staff details
            return await enrichShiftWithDetails(completedShift);
          }
        })
      );

      setCompletedShifts(enrichedShifts);
    } catch (error) {
      console.error('Error loading completed shifts:', error);
      setError('Failed to load completed shifts');
    }
  };

  const loadPendingReviewShifts = async () => {
    try {
      // Get ONLY shifts with 'pending_review' status
      // These are shifts that have been clocked out by staff but not yet reviewed by admin
      
      const statusResponse = await rosterService.getShifts({
        status: 'pending_review'
      });
      const statusPendingShifts = Array.isArray(statusResponse) ? statusResponse : statusResponse.data || [];
      
      // Convert main shifts to completion format for consistency
      const formattedStatusShifts = statusPendingShifts.map(shift => ({
        shiftId: shift.id,
        staffId: shift.staffId,
        clientId: shift.clientId,
        startDateTime: shift.startDateTime,
        endDateTime: shift.endDateTime,
        actualStartTime: shift.actualStartTime,
        actualEndTime: shift.actualEndTime,
        status: 'pending_review',
        isCompletedByStaff: true,
        completedByStaffAt: shift.actualEndTime || shift.updatedAt,
        isReviewedByAdmin: false,
        isAcceptedByAdmin: false,
        createdAt: shift.createdAt,
        updatedAt: shift.updatedAt,
        completionNotes: shift.notes || '',
        ...shift
      }));
      
      // Enrich with additional details
      const enrichedShifts = await Promise.all(
        formattedStatusShifts.map(async (pendingShift) => {
          try {
            return await enrichShiftWithDetails(pendingShift);
          } catch (error) {
            console.warn(`Could not enrich pending shift ${pendingShift.shiftId}:`, error);
            return pendingShift;
          }
        })
      );

      setPendingReviewShifts(enrichedShifts);
    } catch (error) {
      console.error('Error loading pending review shifts:', error);
      setError('Failed to load pending review shifts');
    }
  };

  // Enhanced enrichment function that fetches client and staff details
  const enrichShiftWithDetails = async (shift: any): Promise<any> => {
    try {
      const enrichedShift = { ...shift };
      
      // Fetch client details if missing
      if (shift.clientId && !shift.clientName) {
        try {
          const client = await clientService.getClient(shift.clientId);
          const clientName = getClientDisplayName(client);
          enrichedShift.clientName = clientName;
          enrichedShift.clientDetails = {
            id: client.id,
            name: clientName,
            address: client.address || ''
          };
        } catch (error) {
          console.warn(`Could not fetch client details for ID ${shift.clientId}:`, error);
        }
      }
      
      // Fetch staff details if missing
      if (shift.staffId && !shift.staffName) {
        try {
          const staff = await staffService.getStaffProfile(shift.staffId);
          const staffName = getStaffDisplayName(staff);
          enrichedShift.staffName = staffName;
          enrichedShift.staffDetails = {
            id: staff.id,
            name: staffName,
            employeeId: staff.employeeId || '',
            position: staff.position || ''
          };
        } catch (error) {
          console.warn(`Could not fetch staff details for ID ${shift.staffId}:`, error);
        }
      }
      
      return enrichedShift;
    } catch (error) {
      console.warn('Error enriching shift with details:', error);
      return shift;
    }
  };

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const formatDateTime = (dateTime: string) => {
    return formatDateTimeForUserTimezone(dateTime);
  };

  const formatDuration = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate.getTime() - startDate.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const handleViewDetails = (shift: Shift | ShiftCompletion) => {
    setSelectedShift(shift);
    setIsDetailModalOpen(true);
  };

  const handleEditTime = (shift: Shift | ShiftCompletion) => {
    setSelectedShift(shift);
    
    // Helper function to convert UTC date to local datetime-local format with timezone awareness
    const formatForDateTimeLocal = (dateString: string) => {
      if (!dateString || dateString === '0001-01-01T00:00:00') return '';
      
      try {
        // Parse UTC date and convert to user's timezone
        const userTimezone = getUserTimezone();
        const date = dayjs(dateString).tz(userTimezone);
        
        // Check if date is valid
        if (!date.isValid()) {
          console.warn('Invalid date string:', dateString);
          return '';
        }
        
        // Format for datetime-local input (YYYY-MM-DDTHH:mm)
        const result = date.format('YYYY-MM-DDTHH:mm');
        return result;
      } catch (error) {
        console.error('Error in formatForDateTimeLocal:', error);
        return '';
      }
    };
    
    // Pre-populate with existing actual times or scheduled times as fallback
    const actualStart = ('actualStartTime' in shift && shift.actualStartTime) ? 
      formatForDateTimeLocal(shift.actualStartTime) : 
      formatForDateTimeLocal(shift.startDateTime);
      
    const actualEnd = ('actualEndTime' in shift && shift.actualEndTime) ? 
      formatForDateTimeLocal(shift.actualEndTime) : 
      formatForDateTimeLocal(shift.endDateTime);
    
    setEditTimeData({
      actualStartTime: actualStart,
      actualEndTime: actualEnd,
      notes: ('notes' in shift && shift.notes) ? shift.notes : ''
    });
    setIsTimeEditModalOpen(true);
  };

  const handleReviewShift = (shift: ShiftCompletion) => {
    setSelectedShift(shift);
    setReviewData({
      isAccepted: true,
      adminReviewNotes: ''
    });
    setIsReviewModalOpen(true);
  };

  /** Returns true if the shift is clocked out and within 7 days — staff can still edit. */
  const canEditShiftInfo = (shift: any): boolean => {
    const endTime = shift.actualEndTime || shift.completedByStaffAt;
    if (!endTime) return false;
    const endDate = new Date(endTime.endsWith('Z') ? endTime : endTime + 'Z');
    return endDate > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  };

  const handleOpenEditInfoDialog = (shift: any) => {
    setEditInfoDialog({ open: true, shift });
    setEditInfoWorkInfo((shift as any).workInformation ?? '');
    setEditInfoTravelKm(
      typeof (shift as any).travelKilometres === 'number' ? String((shift as any).travelKilometres) : ''
    );
    setEditInfoError(null);
  };

  const performEditShiftInfo = async () => {
    if (!editInfoDialog.shift) return;
    const shiftId = editInfoDialog.shift.id ?? editInfoDialog.shift.shiftId;
    setEditInfoSaving(true);
    setEditInfoError(null);
    try {
      let kmParsed: number | null = null;
      if (editInfoTravelKm.trim() !== '') {
        const n = Number(editInfoTravelKm);
        if (!Number.isFinite(n) || n < 0) {
          setEditInfoError('Travel kilometres must be a non-negative number.');
          return;
        }
        kmParsed = Math.round(n * 100) / 100;
      }
      await rosterService.staffUpdateShift(
        shiftId,
        currentUserId,
        editInfoWorkInfo.trim() || null,
        kmParsed,
      );
      setSuccess('Shift information updated successfully.');
      setEditInfoDialog({ open: false });
      await loadShiftData();
    } catch (err: any) {
      setEditInfoError(err?.response?.data?.message ?? err?.message ?? 'Failed to update shift information.');
    } finally {
      setEditInfoSaving(false);
    }
  };

  const handleClockIn = async (shiftId: number, staffId: number) => {
    setSaving(true);
    setError(null);
    
    try {
      await rosterService.clockIn(shiftId, staffId, 'Manual clock-in by admin');
      setSuccess('Staff clocked in successfully');
      await loadShiftData();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clock in');
    } finally {
      setSaving(false);
    }
  };

  const handleClockOut = async (shiftId: number, staffId: number) => {
    setSaving(true);
    setError(null);
    
    try {
      // Clock out sets actual end time, status to pending_review, and creates completion record
      console.log('Admin clocking out shift:', shiftId, 'for staff:', staffId);
      const clockOutResult = await rosterService.clockOut(shiftId, staffId, 'Clocked out by admin');
      console.log('Clock out result:', clockOutResult);
      console.log('Status after clock out:', clockOutResult.status);
      
      setSuccess('Shift clocked out successfully - now pending review');
      await loadShiftData();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error during clock out:', err);
      setError(err instanceof Error ? err.message : 'Failed to clock out shift');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTimeEdit = async () => {
    if (!selectedShift) return;
    
    // Type guard to get shift ID
    const shiftId = 'id' in selectedShift && selectedShift.id ? 
      selectedShift.id : 
      ('shiftId' in selectedShift ? selectedShift.shiftId : null);
    if (!shiftId) return;
    
    setSaving(true);
    setError(null);
    
    try {
      // Helper function to convert local datetime to UTC ISO string
      const convertToUTC = (localDateTime: string) => {
        if (!localDateTime) return '';
        // Create date from local datetime value and convert to UTC
        const localDate = new Date(localDateTime);
        return localDate.toISOString();
      };
      
      // Prepare update data - only include fields that should be updated
      const updateData: Partial<Shift> = {};
      
      // Convert local datetime to UTC before sending to server
      if (editTimeData.actualStartTime) {
        updateData.actualStartTime = convertToUTC(editTimeData.actualStartTime);
      }
      
      if (editTimeData.actualEndTime) {
        updateData.actualEndTime = convertToUTC(editTimeData.actualEndTime);
      }
      
      if (editTimeData.notes) {
        updateData.notes = editTimeData.notes;
      }

      // If selectedShift is a full Shift object or ShiftCompletion, preserve core fields
      if ('id' in selectedShift || 'shiftId' in selectedShift) {
        // Preserve essential fields to prevent data loss
        updateData.clientId = selectedShift.clientId;
        updateData.staffId = selectedShift.staffId;
        updateData.startDateTime = selectedShift.startDateTime;
        updateData.endDateTime = selectedShift.endDateTime;
        
        // For regular Shift objects, preserve these additional fields
        if ('id' in selectedShift) {
          updateData.hourlyRate = selectedShift.hourlyRate;
          updateData.shiftType = selectedShift.shiftType;
          updateData.status = selectedShift.status;
        }
        
        // Preserve existing actual times if new ones are not provided (only for Shift objects)
        if ('id' in selectedShift) {
          if (!editTimeData.actualStartTime && selectedShift.actualStartTime) {
            updateData.actualStartTime = selectedShift.actualStartTime;
          }
          
          if (!editTimeData.actualEndTime && selectedShift.actualEndTime) {
            updateData.actualEndTime = selectedShift.actualEndTime;
          }
          
          // Preserve existing notes if new ones are not provided
          if (!editTimeData.notes && selectedShift.notes) {
            updateData.notes = selectedShift.notes;
          }
        }
        
        // Preserve other optional fields if they exist (only for Shift objects)
        if ('id' in selectedShift) {
          if (selectedShift.careInstructions) {
            updateData.careInstructions = selectedShift.careInstructions;
          }
          
          if (selectedShift.specialRequirements) {
            updateData.specialRequirements = selectedShift.specialRequirements;
          }
          
          if (selectedShift.supervisorNotes) {
            updateData.supervisorNotes = selectedShift.supervisorNotes;
          }
        }
      }
      
      await rosterService.updateShift(shiftId, updateData);
      
      setSuccess('Shift times updated successfully');
      setIsTimeEditModalOpen(false);
      
      // Reload only the current tab to preserve enrichment
      switch (tabValue) {
        case 0: // Active Shifts
          await loadActiveShifts();
          break;
        case 1: // Completed Shifts
          await loadCompletedShifts();
          break;
        case 2: // Pending Review
          if (hasAdminPrivileges()) {
            await loadPendingReviewShifts();
          }
          break;
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error updating shift:', err);
      setError(err instanceof Error ? err.message : 'Failed to update shift times');
    } finally {
      setSaving(false);
    }
  };
  const handleSubmitReview = async () => {
    if (!selectedShift || !('shiftId' in selectedShift)) return;
    
    setSaving(true);
    setError(null);
    
    try {
      await rosterService.reviewShift({
        shiftId: selectedShift.shiftId,
        reviewedBy: currentUserId,
        isAccepted: reviewData.isAccepted,
        adminReviewNotes: reviewData.adminReviewNotes
      });
      setSuccess('Shift reviewed successfully');
      setIsReviewModalOpen(false);
      
      // Reload only the current tab to preserve enrichment
      switch (tabValue) {
        case 0: // Active Shifts
          await loadActiveShifts();
          break;
        case 1: // Completed Shifts
          await loadCompletedShifts();
          break;
        case 2: // Pending Review
          if (hasAdminPrivileges()) {
            await loadPendingReviewShifts();
          }
          break;
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Review submission error:', err);
      let errorMessage = 'Failed to review shift';
      
      if (err?.response?.status === 409) {
        // 409 Conflict - shift is not in the right state for review
        const backendMessage = err?.response?.data || err?.message || '';
        if (backendMessage.includes('not pending review')) {
          errorMessage = 'This shift is not ready for review. Only shifts completed by staff and awaiting admin review can be reviewed.';
        } else {
          errorMessage = `Review conflict: ${backendMessage}`;
        }
      } else {
        const backendMessage = err?.response?.data?.message || 
                              err?.response?.data?.title || 
                              err?.message || '';
        if (backendMessage) {
          errorMessage = `Review failed: ${backendMessage}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const renderShiftCard = (shift: Shift) => (
    <Card key={shift.id} sx={{ mb: 2, '&:hover': { boxShadow: 4 } }}>
      <CardContent>
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center" gap={3}>
          {/* Staff and Client Info */}
          <Box flex={1} minWidth={250}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {shift.staffName || `Staff ${shift.staffId}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <ClientIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  {shift.clientName || `Client ${shift.clientId}`}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Time Information */}
          <Box flex={1} minWidth={200}>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center">
                <ScheduleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(shift.startDateTime)}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <TimeIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Duration: {formatDuration(shift.startDateTime, shift.endDateTime)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Status */}
          <Box>
            <Chip
              label={getShiftStatusLabel(shift)}
              color={getShiftStatusColor(shift)}
              size="small"
            />
          </Box>

          {/* Amount */}
          <Box textAlign="right" minWidth={120}>
            <Typography variant="h6" fontWeight="bold">
              <MoneyIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
              ${shift.totalAmount?.toFixed(2) || '0.00'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ${shift.hourlyRate}/hr
            </Typography>
          </Box>

          {/* Actions */}
          <Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <IconButton
                onClick={() => handleViewDetails(shift)}
                color="primary"
                size="small"
                title="View Details"
              >
                <VisibilityIcon />
              </IconButton>

              {hasAdminPrivileges() && (
                <>
                  <IconButton
                    onClick={() => handleEditTime(shift)}
                    color="default"
                    size="small"
                    title="Edit Times"
                  >
                    <EditIcon />
                  </IconButton>

                  {shift.status === 'scheduled' && (
                    <Button
                      onClick={() => handleClockIn(shift.id!, shift.staffId)}
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<ClockInIcon />}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      Clock In
                    </Button>
                  )}

                  {shift.status === 'in_progress' && (
                    <Button
                      onClick={() => handleClockOut(shift.id!, shift.staffId)}
                      variant="contained"
                      color="warning"
                      size="small"
                      startIcon={<ClockOutIcon />}
                      sx={{ minWidth: 'auto', px: 1 }}
                      title="Clock out and complete this shift"
                    >
                      Clock Out & Complete
                    </Button>
                  )}

                  {shift.status === 'scheduled' && (
                    <Button
                      onClick={() => handleClockOut(shift.id!, shift.staffId)}
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CompleteIcon />}
                      sx={{ minWidth: 'auto', px: 1 }}
                      title="Complete this shift"
                    >
                      Complete Shift
                    </Button>
                  )}
                </>
              )}
            </Stack>
            
            {/* Admin indicator */}
            {hasAdminPrivileges() && (
              <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                Admin Controls Available
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderCompletedShiftCard = (shift: ShiftCompletion) => (
    <Card key={shift.shiftId} sx={{ mb: 2, '&:hover': { boxShadow: 4 } }}>
      <CardContent>
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center" gap={3}>
          {/* Staff and Client Info with Status Badge */}
          <Box flex={1} minWidth={250}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box position="relative">
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <PersonIcon />
                </Avatar>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: 2,
                    borderColor: 'background.paper',
                    bgcolor: shift.isAcceptedByAdmin ? 'success.main' : 
                             shift.isReviewedByAdmin ? 'error.main' : 'warning.main'
                  }}
                />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {(shift as any).staffName || (shift as any).staffDetails?.name || `Staff ${shift.staffId}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <ClientIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  {(shift as any).clientName || (shift as any).clientDetails?.name || `Client ${shift.clientId}`}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Time Information */}
          <Box flex={1} minWidth={200}>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center">
                <ScheduleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {shift.startDateTime && shift.startDateTime !== '0001-01-01T00:00:00' 
                    ? formatDateTime(shift.startDateTime) 
                    : 'Start time not available'}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <CompleteIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                <Typography variant="body2" color="success.main">
                  {shift.completedByStaffAt ? formatDateTime(shift.completedByStaffAt) : 'Completed'}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Review Status */}
          <Box>
            <Chip
              label={getShiftStatusLabel(shift)}
              color={getShiftStatusColor(shift)}
              size="small"
            />
          </Box>

          {/* Duration */}
          <Box textAlign="right" minWidth={120}>
            <Typography variant="body2" color="primary" fontWeight="bold">
              <TimeIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              {(() => {
                // Check if actual times are available
                const hasActualTimes = ('actualStartTime' in shift && shift.actualStartTime) && 
                                     ('actualEndTime' in shift && shift.actualEndTime);
                
                if (hasActualTimes) {
                  const actualDuration = formatDuration(
                    (shift as any).actualStartTime, 
                    (shift as any).actualEndTime
                  );
                  return `${actualDuration} (Actual)`;
                } else {
                  const scheduledDuration = formatDuration(shift.startDateTime, shift.endDateTime);
                  return `${scheduledDuration} (Scheduled)`;
                }
              })()}
            </Typography>
            {(() => {
              // Show additional info if we have both actual and scheduled times
              const hasActualTimes = ('actualStartTime' in shift && shift.actualStartTime) && 
                                   ('actualEndTime' in shift && shift.actualEndTime);
              
              if (hasActualTimes) {
                const scheduledDuration = formatDuration(shift.startDateTime, shift.endDateTime);
                return (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {scheduledDuration} scheduled
                  </Typography>
                );
              }
              return null;
            })()}
            {/* Travel km snippet */}
            {typeof (shift as any).travelKilometres === 'number' && (shift as any).travelKilometres > 0 && (
              <Typography variant="caption" color="text.secondary" display="block">
                🚗 {((shift as any).travelKilometres as number).toFixed(1)} km
              </Typography>
            )}
          </Box>

          {/* Work Information snippet */}
          {(shift as any).workInformation && (
            <Box sx={{ maxWidth: 200, display: { xs: 'none', lg: 'block' } }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Work Info
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}
              >
                {(shift as any).workInformation}
              </Typography>
            </Box>
          )}

          {/* Actions */}
          <Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <IconButton
                onClick={() => handleViewDetails(shift)}
                color="primary"
                size="small"
                title="View Details"
              >
                <VisibilityIcon />
              </IconButton>

              {hasAdminPrivileges() && (
                <>
                  <IconButton
                    onClick={() => handleEditTime(shift)}
                    color="default"
                    size="small"
                    title="Edit Times"
                  >
                    <EditIcon />
                  </IconButton>

                  {!shift.isReviewedByAdmin && (shift.status === 'pending_review' || shift.status === 'pendingreview') && (
                    <Button
                      onClick={() => handleReviewShift(shift)}
                      variant="contained"
                      color="warning"
                      size="small"
                      startIcon={<ReviewIcon />}
                      sx={{ minWidth: 'auto', px: 1 }}
                      title="This shift is ready for admin review"
                    >
                      Review
                    </Button>
                  )}
                  
                  {!shift.isReviewedByAdmin && shift.status === 'completed' && (
                    <Button
                      onClick={() => handleReviewShift(shift)}
                      variant="contained"
                      color="warning"
                      size="small"
                      startIcon={<ReviewIcon />}
                      sx={{ minWidth: 'auto', px: 1 }}
                      title="This completed shift needs admin review"
                    >
                      Review
                    </Button>
                  )}
                  
                  {shift.isReviewedByAdmin && (
                    <Chip
                      label="Already Reviewed"
                      size="small"
                      color="success"
                      variant="outlined"
                      title="This shift has been reviewed by admin"
                    />
                  )}
                  
                  {!shift.isReviewedByAdmin && 
                   shift.status !== 'pending_review' && 
                   shift.status !== 'pendingreview' && 
                   shift.status !== 'completed' && (
                    <Chip
                      label="Awaiting Completion"
                      size="small"
                      color="info"
                      variant="outlined"
                      title="Staff must complete this shift before it can be reviewed"
                    />
                  )}
                </>
              )}

              {/* Staff self-edit: only for non-admin, within 7 days */}
              {!hasAdminPrivileges() && canEditShiftInfo(shift) && (
                <Button
                  onClick={() => handleOpenEditInfoDialog(shift)}
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  sx={{ minWidth: 'auto', px: 1 }}
                  title="Edit your work information and travel km"
                >
                  Edit Info
                </Button>
              )}
            </Stack>
            
            {/* Review status indicator */}
            {hasAdminPrivileges() && !shift.isReviewedByAdmin && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                Pending Admin Review
              </Typography>
            )}
            
            {shift.isReviewedByAdmin && (
              <Typography 
                variant="caption" 
                color={getShiftStatusColor(shift) === 'success' ? "success.main" : "error.main"} 
                sx={{ mt: 0.5, display: 'block' }}
              >
                {getShiftStatusLabel(shift)} by Admin
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <PageLayout>
      <Box sx={{ maxWidth: '100%', mx: 'auto', p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Shift Management
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Manage active shifts, completed work, and administrative reviews
          </Typography>
          
          {/* User Role and Capabilities */}
          <Paper sx={{ p: 2, mt: 2, bgcolor: 'background.default' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <Box>
                <Typography variant="subtitle2" color="primary">
                  Your Role: {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                </Typography>
              </Box>
              
              {hasAdminPrivileges() && (
                <Box>
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Chip 
                      icon={<ClockInIcon />} 
                      label="Clock In/Out Staff" 
                      size="small" 
                      variant="outlined" 
                      color="success"
                    />
                    <Chip 
                      icon={<CompleteIcon />} 
                      label="Complete Shifts" 
                      size="small" 
                      variant="outlined" 
                      color="primary"
                    />
                    <Chip 
                      icon={<ReviewIcon />} 
                      label="Review & Approve" 
                      size="small" 
                      variant="outlined" 
                      color="warning"
                    />
                    <Chip 
                      icon={<EditIcon />} 
                      label="Edit Times" 
                      size="small" 
                      variant="outlined" 
                      color="default"
                    />
                  </Stack>
                </Box>
              )}
              
              {!hasAdminPrivileges() && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    View and manage your own shifts
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Box>
        
        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        )}
        
        {/* Success Alert */}
        {success && (
          <Alert 
            severity="success" 
            onClose={() => setSuccess(null)}
            sx={{ mb: 3 }}
          >
            {success}
          </Alert>
        )}
        
        {/* Tab Navigation */}
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={(_, newValue) => handleTabChange(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab 
              label={`Active Shifts (${activeShifts.length})`} 
              id="shift-tab-0"
              aria-controls="shift-tabpanel-0"
            />
            <Tab 
              label={`Completed & Approved (${completedShifts.length})`} 
              id="shift-tab-1"
              aria-controls="shift-tabpanel-1"
            />
            {hasAdminPrivileges() && (
              <Tab 
                label={`Pending Review (${pendingReviewShifts.length})`} 
                id="shift-tab-2"
                aria-controls="shift-tabpanel-2"
              />
            )}
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress size={48} />
            </Box>
          ) : activeShifts.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No active shifts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                There are no active shifts to display.
              </Typography>
            </Paper>
          ) : (
            <Box>
              {activeShifts.map(renderShiftCard)}
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress size={48} />
            </Box>
          ) : completedShifts.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <CompleteIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No completed shifts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                There are no completed shifts to display.
              </Typography>
            </Paper>
          ) : (
            <Box>
              {completedShifts.map(renderCompletedShiftCard)}
            </Box>
          )}
        </TabPanel>

        {hasAdminPrivileges() && (
          <TabPanel value={tabValue} index={2}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress size={48} />
              </Box>
            ) : pendingReviewShifts.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <ReviewIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No shifts pending review
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Shifts appear here after staff complete them and need admin review.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Workflow: Staff Complete Shift → Pending Review → Admin Review → Approved/Rejected
                </Typography>
              </Paper>
            ) : (
              <Box>
                {pendingReviewShifts.map(renderCompletedShiftCard)}
              </Box>
            )}
          </TabPanel>
        )}

        {/* Modals */}
        {/* Shift Details Modal */}
        <Dialog 
          open={isDetailModalOpen} 
          onClose={() => setIsDetailModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Shift Details</DialogTitle>
          <DialogContent>
            {selectedShift && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 1 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>Basic Information</Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Staff</Typography>
                      <Typography variant="body1">
                        {'staffName' in selectedShift ? selectedShift.staffName : `Staff ${selectedShift.staffId}`}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Client</Typography>
                      <Typography variant="body1">
                        {'clientName' in selectedShift ? selectedShift.clientName : `Client ${selectedShift.clientId}`}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip
                        label={getShiftStatusLabel(selectedShift)}
                        color={getShiftStatusColor(selectedShift)}
                        size="small"
                      />
                    </Box>
                  </Stack>
                </Box>
                
                <Box>
                  <Typography variant="h6" gutterBottom>Time Information</Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Scheduled Start</Typography>
                      <Typography variant="body1">{formatDateTime(selectedShift.startDateTime)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Scheduled End</Typography>
                      <Typography variant="body1">{formatDateTime(selectedShift.endDateTime)}</Typography>
                    </Box>
                    {'actualStartTime' in selectedShift && selectedShift.actualStartTime && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Actual Start</Typography>
                        <Typography variant="body1">{formatDateTime(selectedShift.actualStartTime)}</Typography>
                      </Box>
                    )}
                    {'actualEndTime' in selectedShift && selectedShift.actualEndTime && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Actual End</Typography>
                        <Typography variant="body1">{formatDateTime(selectedShift.actualEndTime)}</Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
                
                {/* Notes Section */}
                {(('completionNotes' in selectedShift && selectedShift.completionNotes) ||
                  ('adminReviewNotes' in selectedShift && selectedShift.adminReviewNotes) ||
                  ('workInformation' in selectedShift && (selectedShift as any).workInformation) ||
                  (typeof (selectedShift as any).travelKilometres === 'number' && (selectedShift as any).travelKilometres > 0)) && (
                  <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                    <Typography variant="h6" gutterBottom>Notes & Work Details</Typography>
                    <Stack spacing={2}>
                      {'completionNotes' in selectedShift && selectedShift.completionNotes && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">Completion Notes</Typography>
                          <Typography variant="body1">{selectedShift.completionNotes}</Typography>
                        </Box>
                      )}
                      {(selectedShift as any).workInformation && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">Work Information</Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                            {(selectedShift as any).workInformation}
                          </Typography>
                          {(selectedShift as any).lastStaffEditAt && (
                            <Typography variant="caption" color="text.secondary">
                              Last updated by staff: {new Date(
                                (selectedShift as any).lastStaffEditAt.endsWith('Z')
                                  ? (selectedShift as any).lastStaffEditAt
                                  : (selectedShift as any).lastStaffEditAt + 'Z'
                              ).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {typeof (selectedShift as any).travelKilometres === 'number' && (selectedShift as any).travelKilometres > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">Travel Kilometres</Typography>
                          <Typography variant="body1">{((selectedShift as any).travelKilometres as number).toFixed(2)} km</Typography>
                        </Box>
                      )}
                      {'adminReviewNotes' in selectedShift && selectedShift.adminReviewNotes && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">Admin Review Notes</Typography>
                          <Typography variant="body1">{selectedShift.adminReviewNotes}</Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDetailModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Time Edit Modal */}
        <Dialog 
          open={isTimeEditModalOpen} 
          onClose={() => setIsTimeEditModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Shift Times</DialogTitle>
          <DialogContent>
            {selectedShift && (
              <>
                {/* Shift Information Header */}
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                  <Typography variant="h6" gutterBottom>
                    Shift Information
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Staff</Typography>
                      <Typography variant="body1">
                        {'staffName' in selectedShift ? selectedShift.staffName : `Staff ${selectedShift.staffId}`}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Client</Typography>
                      <Typography variant="body1">
                        {'clientName' in selectedShift ? selectedShift.clientName : `Client ${selectedShift.clientId}`}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Scheduled Start</Typography>
                      <Typography variant="body1">{formatDateTime(selectedShift.startDateTime)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Scheduled End</Typography>
                      <Typography variant="body1">{formatDateTime(selectedShift.endDateTime)}</Typography>
                    </Box>
                  </Box>
                </Paper>
                
                {/* Edit Form */}
                <Stack spacing={3}>
                  <TextField
                    type="datetime-local"
                    label="Actual Start Time"
                    value={editTimeData.actualStartTime}
                    onChange={(e) => setEditTimeData({ ...editTimeData, actualStartTime: e.target.value })}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="Set the actual time the shift started"
                  />
                  <TextField
                    type="datetime-local"
                    label="Actual End Time"
                    value={editTimeData.actualEndTime}
                    onChange={(e) => setEditTimeData({ ...editTimeData, actualEndTime: e.target.value })}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="Set the actual time the shift ended"
                  />
                  
                  {/* Duration Display (Read-only) */}
                  {editTimeData.actualStartTime && editTimeData.actualEndTime && (
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Calculated Duration
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {(() => {
                          try {
                            const start = new Date(editTimeData.actualStartTime);
                            const end = new Date(editTimeData.actualEndTime);
                            const diffMs = end.getTime() - start.getTime();
                            const hours = Math.floor(diffMs / (1000 * 60 * 60));
                            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                            return `${hours}h ${minutes}m`;
                          } catch {
                            return 'Invalid times';
                          }
                        })()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Duration is automatically calculated from start and end times
                      </Typography>
                    </Paper>
                  )}
                  
                  <TextField
                    multiline
                    rows={3}
                    label="Notes"
                    value={editTimeData.notes}
                    onChange={(e) => setEditTimeData({ ...editTimeData, notes: e.target.value })}
                    placeholder="Add any notes about the time changes..."
                    fullWidth
                    helperText="Optional notes about the shift or time adjustments"
                  />
                </Stack>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsTimeEditModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTimeEdit} 
              variant="contained" 
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Shift Info Dialog (Staff self-service) */}
        <Dialog
          open={editInfoDialog.open}
          onClose={() => !editInfoSaving && setEditInfoDialog({ open: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Shift Information</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {editInfoError && <Alert severity="error">{editInfoError}</Alert>}
              <TextField
                multiline
                rows={5}
                label="Work Information"
                value={editInfoWorkInfo}
                onChange={(e) => setEditInfoWorkInfo(e.target.value)}
                placeholder="Describe the work performed during this shift..."
                fullWidth
                helperText="Summary of tasks and activities completed during the shift"
              />
              <TextField
                type="number"
                label="Travel Kilometres"
                value={editInfoTravelKm}
                onChange={(e) => setEditInfoTravelKm(e.target.value)}
                fullWidth
                inputProps={{ min: 0, step: 0.1 }}
                helperText="Distance travelled for this shift (leave blank if none)"
              />
              <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                You can update this information within 7 days of shift completion.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditInfoDialog({ open: false })} disabled={editInfoSaving}>
              Cancel
            </Button>
            <Button
              onClick={performEditShiftInfo}
              variant="contained"
              disabled={editInfoSaving}
              startIcon={editInfoSaving ? <CircularProgress size={16} /> : undefined}
            >
              {editInfoSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Review Modal */}
        <Dialog 
          open={isReviewModalOpen} 
          onClose={() => setIsReviewModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Review Completed Shift</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Review Decision</InputLabel>
                <Select
                  value={reviewData.isAccepted.toString()}
                  onChange={(e) => setReviewData({ ...reviewData, isAccepted: e.target.value === 'true' })}
                  label="Review Decision"
                >
                  <MenuItem value="true">Approve</MenuItem>
                  <MenuItem value="false">Reject</MenuItem>
                </Select>
              </FormControl>
              <TextField
                multiline
                rows={4}
                label="Admin Review Notes"
                value={reviewData.adminReviewNotes}
                onChange={(e) => setReviewData({ ...reviewData, adminReviewNotes: e.target.value })}
                placeholder="Add your review comments..."
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsReviewModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button 
              onClick={handleSubmitReview}
              variant="contained"
              color={reviewData.isAccepted ? 'success' : 'error'}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              {saving ? 'Reviewing...' : (reviewData.isAccepted ? 'Approve' : 'Reject')} Shift
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageLayout>
  );
};

export default ShiftManagementPage;
